import { useMemo, useState } from "react";
import { useStrategies } from "../../../contexts/StrategyContext";

import { getDateAggregationKey, inferGlobalAggregationMode } from "../../../utils/getDateAggregationKey";
import { getColor } from "../../../utils/colorUtils";
import { MultiScatterChart } from "./MultiScatterChart";
import { MultiSummaryStats } from "./MultiSummaryStats";
import { MultiLineChart } from "./MultiLineChart";

interface Props {
    isLoading: boolean;
    onEvaluate: () => void;
}




export function MultiEvaluatePanel({ isLoading, onEvaluate }: Props) {
    const { strategies } = useStrategies();
    const relevant = strategies.filter((s) => s.selectedInAll && s.result);

    const [alignMode, setAlignMode] = useState<"none" | "left" | "right">("none");

    const [connectAcrossNulls, setConnectAcrossNulls] = useState(false);



    const [hoveredKey, setHoveredKey] = useState<string | null>(null);


    const mergedData = useMemo(() => {
        if (alignMode === "left" || alignMode === "right") {
            // 1. compute per-series synthetic indices based on time gaps
            const seriesIndexed: Record<string, { index: number; value: number }[]> = {};
            let globalMaxIndex = 0;

            for (const s of relevant) {
                const data = (s.result ?? []).slice().sort((a: any, b: any) => +new Date(a.time) - +new Date(b.time));
                let idx = 0;
                const mapped: { index: number; value: number }[] = [];

                for (let i = 0; i < data.length; i++) {
                    if (i > 0) {
                        const prev = new Date(data[i - 1].time);
                        const curr = new Date(data[i].time);
                        // difference in days between consecutive timestamps
                        const diffDays = Math.max(
                            1,
                            Math.round((curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24))
                        );
                        if (diffDays > 20) {
                            continue; // skip this point completely
                        }
                        idx += diffDays; // jump ahead by diffDays
                    }
                    mapped.push({ index: idx, value: data[i].value });
                }

                seriesIndexed[s.id] = mapped;
                if (mapped.length > 0) globalMaxIndex = Math.max(globalMaxIndex, mapped[mapped.length - 1].index);
            }

            // 2. compute shift offsets for right alignment
            const shifts: Record<string, number> = {};
            for (const s of relevant) {
                const arr = seriesIndexed[s.id];
                const lastIndex = arr.length ? arr[arr.length - 1].index : 0;
                shifts[s.id] = alignMode === "right" ? globalMaxIndex - lastIndex : 0;
            }

            // 3. build merged data table across union of all shifted indices
            const allIndices = new Set<number>();
            for (const s of relevant) {
                const shift = shifts[s.id];
                for (const p of seriesIndexed[s.id]) {
                    allIndices.add(p.index + shift);
                }
            }

            const sortedIndices = Array.from(allIndices).sort((a, b) => a - b);
            const merged = sortedIndices.map((i) => {
                const row: any = { index: i };
                for (const s of relevant) {
                    const shift = shifts[s.id];
                    const found = seriesIndexed[s.id].find((p) => p.index + shift === i);
                    row[s.id] = found ? found.value : null;
                }
                return row;
            });

            return merged;
        }



        const allKeys = new Set<string>();
        const grouped: { [strategyId: string]: { [key: string]: number } } = {};

        // Pick a representative strategy to infer aggregation strategy
        // Determine global mode based on all strategy results
        const allResults = relevant.map((s) => s.result!).filter(Boolean);
        const mode = inferGlobalAggregationMode(allResults);

        for (const s of relevant) {
            grouped[s.id] = {};
            for (const point of s.result!) {
                const key = getDateAggregationKey(point.time, mode);
                allKeys.add(key);
                grouped[s.id][key] = point.value;
            }
        }

        const sortedKeys = Array.from(allKeys).sort((a, b) => {
            const da = new Date(a);
            const db = new Date(b);

            // --- Special case: Day of month (1904)
            if (da.getFullYear() === 1904 && db.getFullYear() === 1904) {
                return da.getDate() - db.getDate();
            }

            // --- Special case: Hour (1900)
            if (da.getFullYear() === 1900 && db.getFullYear() === 1900) {
                return da.getHours() - db.getHours();
            }

            // --- Everything else: real time ordering
            return da.getTime() - db.getTime();
        });

        return sortedKeys.map((key) => {
            const merged: any = { time: key };
            for (const s of relevant) {
                merged[s.id] = grouped[s.id][key] ?? null;
            }
            return merged;
        });
    }, [relevant, alignMode]);




    const points = useMemo(() => {
        if (relevant.length !== 2) return [];

        return mergedData
            .map((row) => {
                const x = row[relevant[0].id];
                const y = row[relevant[1].id];
                return x != null && y != null ? { x, y } : null;
            })
            .filter(Boolean) as { x: number; y: number }[];
    }, [mergedData, relevant]);


    // --- Compute summary stats and axis assignments ---
    const seriesConfigs = useMemo<Record<string, string>>(() => {
        if (!mergedData || mergedData.length === 0) return {};

        const stats = relevant.map((s) => {
            const values = mergedData
                .map((row) => row[s.id])
                .filter((v) => v != null && !isNaN(v));

            if (values.length === 0) return { id: s.id, nature: s.nature || "generic", range: 0, max: 0 };
            const max = Math.max(...values);
            const min = Math.min(...values);
            return { id: s.id, nature: s.nature || "generic", range: Math.abs(max - min), max: Math.abs(max) };
        });

        // 10x Magnitude Rule:
        // Group by nature, then check if any series in the group is 10x larger than others.
        const assignments: Record<string, string> = {};
        const natures = Array.from(new Set(stats.map(s => s.nature)));

        natures.forEach(n => {
            const group = stats.filter(s => s.nature === n);
            if (group.length <= 1) {
                assignments[group[0]?.id] = (n === "price" || n === "fundamental") ? "left" : "right";
                return;
            }

            const sorted = [...group].sort((a, b) => b.max - a.max);
            const largest = sorted[0];
            const smallest = sorted[sorted.length - 1];

            // If the largest is > 10x the smallest, put the largest on its own axis
            if (smallest.max > 0 && largest.max / smallest.max > 10) {
                sorted.forEach((s, idx) => {
                    if (idx === 0) assignments[s.id] = "right"; // Outlier on right
                    else assignments[s.id] = "left"; // Others on left
                });
            } else {
                // Standard nature-based default
                group.forEach(s => {
                    assignments[s.id] = (n === "price" || n === "fundamental") ? "left" : "right";
                });
            }
        });

        return assignments;
    }, [mergedData, relevant]);

    const summaryStats = useMemo(() => {
        if (!mergedData || mergedData.length === 0) return [];

        return relevant.map((s) => {
            const values = mergedData
                .map((row) => ({
                    x: alignMode === "none" ? row.time : row.index,
                    y: row[s.id],
                }))
                .filter((p) => p.y != null && !isNaN(p.y));

            if (values.length === 0) return null;

            const ys = values.map((v) => v.y);
            const n = ys.length;

            const max = Math.max(...ys);
            const min = Math.min(...ys);
            const mean = ys.reduce((a, b) => a + b, 0) / n;
            const sorted = [...ys].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
            const variance = ys.reduce((s, y) => s + (y - mean) ** 2, 0) / n;
            const std = Math.sqrt(variance);
            const range = max - min;
            const first = ys[0];
            const last = ys[ys.length - 1];
            const delta = last - first;
            const pctChange = first ? (delta / Math.abs(first)) * 100 : 0;
            const cv = mean !== 0 ? std / Math.abs(mean) : null;

            const maxX = values.find(v => v.y === max)?.x ?? null;
            const minX = values.find(v => v.y === min)?.x ?? null;

            return {
                name: s.name,
                n,
                first,
                last,
                delta,
                pctChange,
                max,
                maxX,
                min,
                minX,
                range,
                mean,
                median,
                std,
                cv,
            };
        }).filter(Boolean);
    }, [mergedData, relevant, alignMode]);


    return (
        <div style={{ padding: "1rem" }}>
            <button
                onClick={onEvaluate}
                disabled={isLoading}
                style={{ backgroundColor: "var(--accent-success)", color: "var(--text-primary)", marginBottom: "1rem" }}
            >
                {isLoading ? "Calculating..." : "\u{1F4C5} Calculate"}
            </button>

            <div style={{ marginLeft: "1rem", display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                <label>Align:</label>
                <select
                    value={alignMode}
                    onChange={(e) => setAlignMode(e.target.value as "none" | "left" | "right")}
                    style={{ padding: "0.2rem 0.4rem", borderRadius: "4px", border: "1px solid var(--surface-glass-border)" }}
                >
                    <option value="none">By time</option>
                    <option value="left">By index (left)</option>
                    <option value="right">By index (right)</option>
                </select>
            </div>

            <label style={{ marginLeft: "1rem" }}>
                <input
                    type="checkbox"
                    checked={connectAcrossNulls}
                    onChange={() => setConnectAcrossNulls((v) => !v)}
                />{" "}
                Connect across nulls
            </label>


            {mergedData.length > 0 && (
                <MultiLineChart
                    mergedData={mergedData}
                    relevant={relevant}
                    alignMode={alignMode}
                    connectAcrossNulls={connectAcrossNulls}
                    hoveredKey={hoveredKey}
                    setHoveredKey={setHoveredKey}
                    seriesConfigs={seriesConfigs}
                    getColor={getColor}
                />
            )}

            {points.length > 0 && (
                <MultiScatterChart points={points} relevant={relevant} />
            )}

            {summaryStats.length > 0 && (
                <MultiSummaryStats
                    summaryStats={summaryStats}
                    alignMode={alignMode}
                    getColor={getColor}
                    hoveredKey={hoveredKey}
                />
            )}

        </div>
    );


}


